import { FcGeneratorOptions, FcSvg } from '@dtx-company/flowcode-generator-types/src'
import { logger } from '@dtx-company/logger'
import PromiseWorker from 'promise-worker'
import type { GeneratorWorkerInput, GeneratorWorkerOutput } from './workers/generator.worker'

let promiseWorker: PromiseWorker | undefined

export async function fcGenerateFlowcode(options: FcGeneratorOptions): Promise<FcSvg> {
  // detect if cache is available
  if (self.caches) {
    const cache = await self.caches.open('flowcode')
    // The caches API expects a request/URL as the key. We use a dummy URL with a hash of the options as the search param.
    // The URL host doesn't matter; it's not used for a request.
    const requestURL = new URL('https://generator-dummy-url.com/flowcode/generate')
    const hash = await digestMessage(JSON.stringify(options))
    requestURL.searchParams.set('opts', hash)
    const cacheKey = requestURL.toString()
    const cached = await cache.match(cacheKey)
    if (cached) {
      return FcSvg.fromJson(await cached.json())
    } else {
      const svg = await generateFlowcode()
      const response = new Response(JSON.stringify(svg), {
        headers: { 'Content-Type': 'application/json' }
      })
      cache.put(cacheKey, response.clone())
      return svg
    }
  } else {
    return generateFlowcode()
  }

  async function generateFlowcode(): Promise<FcSvg> {
    // detect if Web workers are available
    if (self.Worker) {
      if (!promiseWorker) {
        const worker = new Worker(new URL('./workers/generator.worker', import.meta.url), {
          type: 'module'
        })
        promiseWorker = new PromiseWorker(worker)
      }
      const result = await promiseWorker.postMessage<GeneratorWorkerOutput, GeneratorWorkerInput>({
        options
      })
      if (result.ok) {
        return FcSvg.fromJson(result.svg)
      } else {
        logger.logError(result.error, {
          team: 'templates',
          name: 'Worker could not generate flowcode.'
        })
        throw new Error('Worker could not generate Flowcode.')
      }
    } else {
      // fallback
      const mod = await import('@dtx-company/flowcode-generator-v2/src')
      return mod.fcGenerateFlowcode(options)
    }
  }
}

async function digestMessage(message: string): Promise<string> {
  const msgUint8 = new TextEncoder().encode(message)
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  return hashHex
}
